import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getCorruptedEvents, getStats } from '../api';
import { formatTimestamp, formatRelativeTime, getEventTypeLabel, getEventTypeIcon } from '../utils';
import { parseCorruptionDetails, getCorruptionSeverity, getSeverityColor } from '../utils/corruption';
import type { SyncEvent, SyncEventStats } from '../types';
import styles from './Corrupted.module.css';

/**
 * Corrupted Events Page
 * 
 * Focus view on events with corruption detected.
 */

type SortOption = 'timestamp' | 'firmware' | 'severity' | 'user';

export default function Corrupted() {
  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [stats, setStats] = useState<SyncEventStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<SyncEvent | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('timestamp');
  const [selectedFirmware, setSelectedFirmware] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      setError(null);

      try {
        const [eventsData, statsData] = await Promise.all([
          getCorruptedEvents({ page_size: 100, ordering: '-timestamp' }),
          getStats().catch(() => null),
        ]);

        setEvents(eventsData.results || []);
        setStats(statsData);
      } catch (err) {
        console.error('[Corrupted] Error fetching:', err);
        setError('Failed to load corrupted events');
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  // Group corruption types
  const corruptionTypeCounts: Record<string, number> = {};
  events.forEach((event) => {
    if (event.suspicious_details) {
      const findings = parseCorruptionDetails(event.suspicious_details);
      findings.forEach((finding) => {
        corruptionTypeCounts[finding.category] = (corruptionTypeCounts[finding.category] || 0) + 1;
      });
    }
  });

  // Count affected users and devices
  const affectedUsers = new Set(events.map((e) => e.user_email).filter(Boolean)).size;
  const affectedDevices = new Set(events.map((e) => e.device_code).filter(Boolean)).size;

  // Extract unique firmware versions with counts
  const firmwareVersions = useMemo(() => {
    const counts: Record<string, number> = {};
    events.forEach((e) => {
      const fw = e.firmware_version || 'Unknown';
      counts[fw] = (counts[fw] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([a], [b]) => b.localeCompare(a, undefined, { numeric: true }))
      .map(([version, count]) => ({ version, count }));
  }, [events]);

  // Toggle firmware filter
  const toggleFirmwareFilter = (version: string) => {
    setSelectedFirmware((prev) => {
      const next = new Set(prev);
      if (next.has(version)) {
        next.delete(version);
      } else {
        next.add(version);
      }
      return next;
    });
  };

  // Clear all firmware filters
  const clearFirmwareFilter = () => {
    setSelectedFirmware(new Set());
  };

  // Filter and sort events
  const filteredAndSortedEvents = useMemo(() => {
    // First filter by firmware
    let filtered = events;
    if (selectedFirmware.size > 0) {
      filtered = events.filter((e) => {
        const fw = e.firmware_version || 'Unknown';
        return selectedFirmware.has(fw);
      });
    }

    // Then sort
    const sorted = [...filtered];
    switch (sortBy) {
      case 'firmware':
        return sorted.sort((a, b) => {
          const fwA = a.firmware_version || '';
          const fwB = b.firmware_version || '';
          if (fwA === fwB) {
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          }
          return fwB.localeCompare(fwA, undefined, { numeric: true });
        });
      case 'severity':
        return sorted.sort((a, b) => {
          const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          const findingsA = parseCorruptionDetails(a.suspicious_details);
          const findingsB = parseCorruptionDetails(b.suspicious_details);
          const sevA = getCorruptionSeverity(findingsA, a.suspicious_char_count);
          const sevB = getCorruptionSeverity(findingsB, b.suspicious_char_count);
          const orderA = severityOrder[sevA as keyof typeof severityOrder] ?? 4;
          const orderB = severityOrder[sevB as keyof typeof severityOrder] ?? 4;
          if (orderA === orderB) {
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          }
          return orderA - orderB;
        });
      case 'user':
        return sorted.sort((a, b) => {
          const userA = a.user_email || '';
          const userB = b.user_email || '';
          if (userA === userB) {
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          }
          return userA.localeCompare(userB);
        });
      case 'timestamp':
      default:
        return sorted.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
    }
  }, [events, sortBy, selectedFirmware]);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading corrupted events...</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>⚠️</div>
        <div>
          <h1 className={styles.title}>Corrupted Events</h1>
          <p className={styles.subtitle}>
            Events with suspicious characters or data corruption detected.
          </p>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.summary}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{events.length}</div>
          <div className={styles.summaryLabel}>Total Corrupted</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{stats?.corruption_last_24h ?? '-'}</div>
          <div className={styles.summaryLabel}>Last 24 Hours</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{affectedUsers}</div>
          <div className={styles.summaryLabel}>Affected Users</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{affectedDevices}</div>
          <div className={styles.summaryLabel}>Affected Devices</div>
        </div>
      </div>

      {Object.keys(corruptionTypeCounts).length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Corruption by Type</h2>
          <div className={styles.typeGrid}>
            {Object.entries(corruptionTypeCounts)
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => (
                <div key={type} className={styles.typeCard}>
                  <div className={styles.typeName}>{type}</div>
                  <div className={styles.typeCount}>{count}</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Firmware Version Filter */}
      {firmwareVersions.length > 1 && (
        <div className={styles.section}>
          <div className={styles.filterHeader}>
            <h2 className={styles.sectionTitle}>Filter by Firmware</h2>
            {selectedFirmware.size > 0 && (
              <button className={styles.clearFilter} onClick={clearFirmwareFilter}>
                Clear filter
              </button>
            )}
          </div>
          <div className={styles.firmwareFilter}>
            {firmwareVersions.map(({ version, count }) => (
              <button
                key={version}
                className={`${styles.firmwareTag} ${selectedFirmware.has(version) ? styles.firmwareTagActive : ''}`}
                onClick={() => toggleFirmwareFilter(version)}
              >
                {version} <span className={styles.firmwareCount}>({count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            Corrupted Events ({filteredAndSortedEvents.length}
            {selectedFirmware.size > 0 && ` of ${events.length}`})
          </h2>
          <div className={styles.sortControl}>
            <label htmlFor="sortBy">Sort by:</label>
            <select
              id="sortBy"
              className={styles.sortSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
            >
              <option value="timestamp">Most Recent</option>
              <option value="firmware">Firmware Version</option>
              <option value="severity">Severity</option>
              <option value="user">User</option>
            </select>
          </div>
        </div>
        
        {filteredAndSortedEvents.length === 0 ? (
          <div className={styles.placeholder}>
            {events.length === 0 ? (
              <p>🎉 No corrupted events found!</p>
            ) : (
              <p>No events match the selected firmware filter</p>
            )}
          </div>
        ) : (
          <div className={styles.eventList}>
            {filteredAndSortedEvents.map((event) => {
              const findings = parseCorruptionDetails(event.suspicious_details);
              const severity = getCorruptionSeverity(findings, event.suspicious_char_count);
              
              return (
                <div 
                  key={event.id} 
                  className={`${styles.eventCard} ${selectedEvent?.id === event.id ? styles.selected : ''}`}
                  onClick={() => setSelectedEvent(selectedEvent?.id === event.id ? null : event)}
                >
                  <div className={styles.eventHeader}>
                    <div className={styles.eventMeta}>
                      <span className={styles.eventIcon}>{getEventTypeIcon(event.event_type)}</span>
                      <span className={styles.eventType}>{getEventTypeLabel(event.event_type)}</span>
                      <span className={styles.separator}>•</span>
                      <span className={styles.eventTime}>{formatRelativeTime(event.timestamp)}</span>
                    </div>
                    <div 
                      className={styles.severityBadge}
                      style={{ backgroundColor: `${getSeverityColor(severity)}20`, color: getSeverityColor(severity) }}
                    >
                      {severity.toUpperCase()}
                    </div>
                  </div>
                  
                  <div className={styles.eventDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>User:</span>
                      <span>{event.user_email || 'Unknown'}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Device:</span>
                      <span className={styles.deviceCode}>{event.device_code || 'Unknown'}</span>
                      {event.device_name && <span className={styles.deviceName}>({event.device_name})</span>}
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Firmware:</span>
                      <span className={styles.firmwareVersion}>{event.firmware_version || 'Unknown'}</span>
                    </div>
                    {event.file_name && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>File:</span>
                        <span>{event.file_name}</span>
                      </div>
                    )}
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Suspicious chars:</span>
                      <span>{event.suspicious_char_count?.toLocaleString() ?? 'Unknown'}</span>
                    </div>
                  </div>

                  <div className={styles.findings}>
                    {findings.map((finding, idx) => (
                      <div key={idx} className={styles.findingTag}>
                        {finding.category}
                      </div>
                    ))}
                  </div>

                  {selectedEvent?.id === event.id && (
                    <div className={styles.expandedDetails}>
                      <div className={styles.expandedSection}>
                        <h4>Full Timestamp</h4>
                        <p>{formatTimestamp(event.timestamp)}</p>
                      </div>
                      
                      <div className={styles.expandedSection}>
                        <h4>Corruption Details</h4>
                        <pre className={styles.detailsPre}>{event.suspicious_details}</pre>
                      </div>
                      
                      {event.content_raw && (
                        <div className={styles.expandedSection}>
                          <h4>Content Preview (first 500 chars)</h4>
                          <pre className={styles.contentPre}>
                            {event.content_raw.slice(0, 500)}
                            {event.content_raw.length > 500 && '...'}
                          </pre>
                        </div>
                      )}

                      <div className={styles.expandedSection}>
                        <h4>IDs</h4>
                        <p><strong>Event ID:</strong> {event.id}</p>
                        <p><strong>Session ID:</strong> {event.session_id}</p>
                        {event.file_id && <p><strong>File ID:</strong> {event.file_id}</p>}
                      </div>

                      <div className={styles.expandedActions}>
                        <Link 
                          to={`/events/${event.id}`} 
                          className={styles.viewDetailsLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          🔍 View Full Event Details
                        </Link>
                        <Link 
                          to={`/events/${event.id}?tab=session`} 
                          className={styles.viewSessionLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          📋 View Session Context
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
